import {
  calculateBubbleTopBasedOnCurrentDiscussions,
  getFilteredDiscussions,
  groupDiscussionsByTop,
  isNewCommentBubble
} from "@/components/designSystem/Discussions/utils.ts"
import { Discussion, NewCommentBubble } from "@/models/Discussions.ts"
import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import _ from "lodash"

export interface DiscussionWithTop extends Discussion {
  topPosition?: number
}

export enum FilterType {
  OPEN = "open",
  WAITING = "waiting",
  NONE = "none"
}

interface State {
  discussions: (Discussion | NewCommentBubble)[]
  allDiscussionsWithTop: DiscussionWithTop[]
  filteredDiscussions: DiscussionWithTop[]
  groupedDiscussions: Record<number, DiscussionWithTop[]>
  manualRecalculationTrigger: boolean
  discussionsExpanded: boolean
  expandedStateBeforeComment: boolean
  currentFilter: FilterType
  toggleExpandDiscussionsContainer: () => void
  expandDiscussionsContainer: () => void
  minimizeDiscussionsContainer: () => void
  toggleManualRecalculationTrigger: () => void
  setDiscussionsWithTop: (discussions: DiscussionWithTop[]) => void
  injectNewCommentBubbleToDiscussions: (newCommentBubble: NewCommentBubble) => NewCommentBubble
  removeNewCommentBubbleFromDiscussions: (newCommentBubble: Discussion | NewCommentBubble) => void
  currentHoveredDiscussion: Discussion | null
  setCurrentHoveredDiscussion: (discussion: Discussion) => void
  clearCurrentHoveredDiscussion: () => void
  currentOpenedDiscussion: Discussion | NewCommentBubble | null
  setCurrentOpenedDiscussion: (discussion: Discussion | NewCommentBubble) => void
  clearCurrentOpenedDiscussion: () => void
  applyFilter: (filterType: FilterType) => void
}

const debouncedPositionUpdate = _.debounce(
  (positions: { id: string; top: number }[], updateFn: (positions: { id: string; top: number }[]) => void) => {
    updateFn(positions)
  },
  250
)

export const useDiscussions = create<State>()(
  immer((set, get) => {
    const triggerManualRecalculation = () => {
      debouncedPositionUpdate([], () => {
        set((state) => {
          state.manualRecalculationTrigger = !state.manualRecalculationTrigger
        })
      })
    }

    const expandDiscussions = () => {
      set((state) => {
        state.discussionsExpanded = true
        state.expandedStateBeforeComment = true
      })
      triggerManualRecalculation()
    }

    return {
      discussions: [],
      allDiscussionsWithTop: [],
      filteredDiscussions: [],
      groupedDiscussions: {},
      manualRecalculationTrigger: false,
      discussionsExpanded: false,
      expandedStateBeforeComment: false,
      currentHoveredDiscussion: null,
      currentOpenedDiscussion: null,
      currentFilter: FilterType.NONE,

      toggleExpandDiscussionsContainer: () => {
        set((state) => {
          state.discussionsExpanded = !state.discussionsExpanded
          state.expandedStateBeforeComment = !state.expandedStateBeforeComment
        })
        triggerManualRecalculation()
      },

      expandDiscussionsContainer: () => {
        set((state) => {
          state.discussionsExpanded = true
        })
      },

      minimizeDiscussionsContainer: () => {
        set((state) => {
          state.discussionsExpanded = false
        })
      },

      toggleManualRecalculationTrigger: () => {
        triggerManualRecalculation()
      },

      applyFilter: (filterType) => {
        set((state) => {
          state.currentFilter = filterType

          state.filteredDiscussions = getFilteredDiscussions(state.allDiscussionsWithTop, filterType)
        })
        expandDiscussions()
      },

      setDiscussionsWithTop: (discussions: DiscussionWithTop[]) => {
        set((state) => {
          state.allDiscussionsWithTop = discussions.sort((a, b) => {
            const getPosition = (disc: typeof a) => {
              if (isNewCommentBubble(disc)) return 0
              return (disc as DiscussionWithTop).topPosition || 0
            }
            return getPosition(a) - getPosition(b)
          })

          state.filteredDiscussions = getFilteredDiscussions(state.allDiscussionsWithTop, state.currentFilter)

          state.groupedDiscussions = groupDiscussionsByTop(state.filteredDiscussions)
        })
      },

      injectNewCommentBubbleToDiscussions: (newCommentBubble) => {
        const discussions = get().filteredDiscussions
        const { bubbleIndex } = calculateBubbleTopBasedOnCurrentDiscussions(discussions, newCommentBubble)

        set((state) => {
          state.filteredDiscussions = [
            ...discussions.slice(0, bubbleIndex),
            newCommentBubble,
            ...discussions.slice(bubbleIndex)
          ]
        })

        return newCommentBubble
      },

      removeNewCommentBubbleFromDiscussions: (newCommentBubble) => {
        const discussions = get().filteredDiscussions
        const filtered = discussions.filter((discussion) => discussion.id !== newCommentBubble.id)

        set((state) => {
          state.filteredDiscussions = filtered
        })
      },

      setCurrentHoveredDiscussion: (discussion: Discussion) => {
        set((state) => {
          state.currentHoveredDiscussion = discussion
        })
      },

      clearCurrentHoveredDiscussion: () => {
        set((state) => {
          state.currentHoveredDiscussion = null
        })
      },

      setCurrentOpenedDiscussion: (discussion: Discussion | NewCommentBubble) => {
        set((state) => {
          state.currentOpenedDiscussion = discussion
        })
      },

      clearCurrentOpenedDiscussion: () => {
        set((state) => {
          state.currentOpenedDiscussion = null
        })
      }
    }
  })
)
